import React, { useRef, Children, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { SmartphoneIcon, ShieldCheckIcon, FileTextIcon, GlobeIcon, CpuIcon, ActivityIcon, BarChart3Icon, MapPinIcon, UsersIcon, DropletIcon, ZapIcon, HomeIcon } from 'lucide-react';
type IconComponent = ComponentType<{
  className?: string;
}>;
const iconMap: Record<string, IconComponent> = {
  SmartphoneIcon,
  ShieldCheckIcon,
  FileTextIcon,
  GlobeIcon,
  CpuIcon,
  ActivityIcon,
  BarChart3Icon,
  MapPinIcon,
  UsersIcon,
  DropletIcon,
  ZapIcon,
  HomeIcon
};
export interface HowItWorksStep {
  icon: string;
  title: string;
  description: string;
}
interface HowCarbonSafeWorksProps {
  verticalName: string;
  subtext: string;
  steps: HowItWorksStep[];
}
export function HowCarbonSafeWorks({
  verticalName,
  subtext,
  steps
}: HowCarbonSafeWorksProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28 border-t border-b border-carbon-geo" data-id="element-644">

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-645">
        {/* Header */}
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-646">

          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-647">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-648">
            How CarbonSafe works for{' '}
            <span className="text-carbon-bright" data-id="element-649">{verticalName}</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-650">
            {subtext}
          </p>
        </motion.div>

        {/* Steps flow */}
        <div className="relative" data-id="element-651">
          {/* Desktop connecting line */}
          <div className="hidden lg:block absolute top-[42px] left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-carbon-bright/10 via-carbon-bright/30 to-carbon-bright/10" aria-hidden="true" data-id="element-652" />


          {/* Mobile connecting line */}
          <div className="lg:hidden absolute top-[42px] bottom-[42px] left-[42px] w-0.5 bg-gradient-to-b from-carbon-bright/10 via-carbon-bright/30 to-carbon-bright/10" aria-hidden="true" data-id="element-653" />


          <motion.div className="grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-6" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.15
            }
          }
        }} data-id="element-654">

            {steps.map((step, i) => {
            const IconComponent = iconMap[step.icon];
            return <motion.div key={step.title} className="relative flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0 lg:text-center" variants={{
              hidden: {
                opacity: 0,
                y: 30
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} data-id="element-655">

                  {/* Step circle with number + icon */}
                  <div className="relative z-10 flex-shrink-0" data-id="element-656">
                    <div className="w-[84px] h-[84px] rounded-full bg-carbon-bright/5 border-2 border-carbon-bright/20 flex items-center justify-center" data-id="element-657">
                      {IconComponent && <IconComponent className="w-8 h-8 text-carbon-bright" data-id="element-658" />}
                    </div>
                    {/* Number badge */}
                    <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-carbon-bright text-white text-xs font-bold flex items-center justify-center shadow-md shadow-carbon-bright/30" data-id="element-659">
                      {i + 1}
                    </div>
                  </div>

                  {/* Text */}
                  <div className="lg:mt-5 min-w-0" data-id="element-660">
                    <h3 className="text-base font-bold text-carbon-deep mb-1.5" data-id="element-661">
                      {step.title}
                    </h3>
                    <p className="text-sm text-carbon-mid leading-relaxed" data-id="element-662">
                      {step.description}
                    </p>
                  </div>

                  {/* Desktop arrow between steps */}
                  {i < steps.length - 1 && <div className="hidden lg:block absolute top-[42px] left-[calc(50%+50px)] w-[calc(100%-60px)]" aria-hidden="true" data-id="element-663">

                      <svg className="w-full h-3" viewBox="0 0 100 12" preserveAspectRatio="none" data-id="element-664">

                        <path d="M0 6 L92 6 M86 2 L94 6 L86 10" stroke="#52B788" strokeWidth="1.5" strokeOpacity="0.4" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-665" />

                      </svg>
                    </div>}
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Platform identity footer */}
        <motion.div className="mt-14 flex items-center justify-center" initial={{
        opacity: 0,
        y: 15
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.8,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-666">

          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-carbon-deep/5 border border-carbon-geo" data-id="element-667">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center flex-shrink-0" data-id="element-668">
              <span className="text-white text-[10px] font-bold" data-id="element-669">CS</span>
            </div>
            <p className="text-sm text-carbon-mid" data-id="element-670">
              Powered by{' '}
              <span className="font-bold text-carbon-deep" data-id="element-671">CarbonSafe</span>{' '}
              independent dMRV platform
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}